import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Avatar, type ComboboxItem } from '@node-space/ui-components'
import { useGetCustomersWalletProfileQuery } from 'hooks/queries/Customers/useCustomerWalletProfilesQuery'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getCryptoCurrencies } from 'services/CurrencyService'
import { LedgerWalletType, type LedgerType } from 'types/customers'

const SUPPORTED_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD']

const buildCurrencyOption = (code: string): ComboboxItem => ({
  value: code,
  leftText: code,
  LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
})

const ALL_CURRENCIES_VALUE = ''

export const useCurrencyFilterOptions = (activeTab: LedgerType, customerReference?: string) => {
  const { t } = useTranslation()
  const { enableWalletCreationV2 } = useFeatureFlags()
  const isCryptoTab = activeTab === LedgerWalletType.CRYPTO

  const { data: cryptoCurrencies, isPending: isFetchingCrypto } = useQuery({
    queryKey: reactQueryKeys.cryptoCurrencies(),
    queryFn: getCryptoCurrencies,
    enabled: isCryptoTab,
    refetchOnReconnect: false,
  })

  const { data: walletProfiles, isFetching: isFetchingWalletProfiles } =
    useGetCustomersWalletProfileQuery(
      enableWalletCreationV2 && customerReference ? { customerId: customerReference } : undefined
    )

  const profileCurrencies = useMemo(() => {
    if (!walletProfiles?.profiles) return { fiatCurrencies: [], cryptoCurrencies: [] }

    const fiatCurrencies = new Set<string>()
    const cryptoCurrencies = new Set<string>()

    walletProfiles.profiles.forEach(profile => {
      const isCryptoProfile = profile?.id?.startsWith('crypto:')
      profile.currencies?.forEach(currency => {
        if (isCryptoProfile) {
          cryptoCurrencies.add(currency)
        } else {
          fiatCurrencies.add(currency)
        }
      })
    })

    return {
      fiatCurrencies: Array.from(fiatCurrencies).sort((a, b) => a.localeCompare(b)),
      cryptoCurrencies: Array.from(cryptoCurrencies).sort((a, b) => a.localeCompare(b)),
    }
  }, [walletProfiles])

  const currencyOptions: ComboboxItem[] = useMemo(() => {
    const allCurrenciesOption: ComboboxItem = {
      value: ALL_CURRENCIES_VALUE,
      leftText: t('customers.allCurrencies'),
    }

    if (enableWalletCreationV2 && walletProfiles?.profiles?.length) {
      if (isCryptoTab) {
        const cryptoOptions = profileCurrencies.cryptoCurrencies.map(buildCurrencyOption)
        return [allCurrenciesOption, ...cryptoOptions]
      } else {
        const fiatOptions = profileCurrencies.fiatCurrencies.map(buildCurrencyOption)
        return [allCurrenciesOption, ...fiatOptions]
      }
    }

    if (isCryptoTab) {
      const cryptoOptions = (cryptoCurrencies ?? [])
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code))
        .map(currency => buildCurrencyOption(currency.code))

      return [allCurrenciesOption, ...cryptoOptions]
    }

    return [allCurrenciesOption, ...SUPPORTED_FIAT_CURRENCIES.map(buildCurrencyOption)]
  }, [isCryptoTab, cryptoCurrencies, enableWalletCreationV2, profileCurrencies, walletProfiles, t])

  const isFetchingCurrencies = useMemo(() => {
    if (enableWalletCreationV2) {
      if (isCryptoTab && !profileCurrencies.cryptoCurrencies.length) {
        return isFetchingCrypto || isFetchingWalletProfiles
      }
      return isFetchingWalletProfiles
    }

    if (isCryptoTab) {
      return isFetchingCrypto
    }

    return false
  }, [
    isCryptoTab,
    isFetchingCrypto,
    enableWalletCreationV2,
    isFetchingWalletProfiles,
    profileCurrencies,
  ])

  return { currencyOptions, isFetchingCurrencies }
}
